import {
  ParameterExpression,
  type ParameterValue,
} from '@models/reference-range';
import { RISK_NAME_MAP } from '@models/reference-range';

import type {
  ParameterModel,
  ReferenceRangeModel,
  ReferenceRangeRiskLevel,
} from 'generated/types';
import { ParameterType } from 'generated/types';

const { Between, Gt, Gte, Lt, Lte } = ParameterExpression;

export const getReferenceRangeMap = (referenceRange: ReferenceRangeModel) => {
  return new Map(referenceRange.risks.map((risk) => [risk.risk, risk]));
};

/**
 * Determine expression field value for ref ranges
 * * Defaults to Between if there is no custom range
 * @param params Lower & upper bounds, and whether they are inclusive (inclusive = or equal)
 * @returns Options are: Between, Gt, Gte, Lt, Lte
 */
export const determineExpression = (
  parameter: ParameterModel
): ParameterExpression => {
  const lower = parameter.lower_bound;
  const upper = parameter.upper_bound;
  const lower_inclusive = parameter.lower_bound?.inclusive;
  const upper_inclusive = parameter.upper_bound?.inclusive;

  // Scenario 1: lower is T & upper is T => Between
  if (lower && upper) {
    return Between;
  } else if (typeof lower_inclusive === 'boolean' && !upper_inclusive) {
    // Scenario 2: lower is boolean & upper is falsy => Gt/Gte
    return lower_inclusive ? Gte : Gt;
  } else if (typeof upper_inclusive === 'boolean' && !lower_inclusive) {
    // Scenario 3: upper is boolean & lower is falsy => Lt/Lte
    return upper_inclusive ? Lte : Lt;
  }
  // Fallback to Between
  return Between;
};

export const determineInclusivity = (parameter: ParameterValue) => {
  const { expression, lower_bound, upper_bound } = parameter;
  let result = {
    lower_inclusive: true,
    upper_inclusive: true,
  };
  if (expression === Between && lower_bound && upper_bound) {
    return result;
  } else if (lower_bound && !upper_bound) {
    result = {
      lower_inclusive: expression === Gt ? false : true,
      upper_inclusive: true,
    };
  } else if (upper_bound && !lower_bound) {
    result = {
      lower_inclusive: true,
      upper_inclusive: expression === Lt ? false : true,
    };
  }
  return result;
};

/**
 * Retrieve the messageId of the risk level for a metric
 * @param code Metric code
 * @param risk Risk level
 * @returns MessageId of the metric's risk level
 */
export function displayFriendlyRiskLevel(
  code: string,
  risk: ReferenceRangeRiskLevel
): string | undefined {
  const riskMap = RISK_NAME_MAP.get(risk);
  const messageId = riskMap?.get(code) ?? riskMap?.get(ParameterType.default);
  return messageId;
}

/**
 * Checks for overlaps or gaps in parameter form lower and upper bounds
 * compared to next form
 * @param current Value in current risk form
 * @param next Value in next risk form
 * @returns Undefined, if there is no next value. Current and next values, along with error type
 */
export function checkParamGapOrOverlap(
  current: ParameterValue,
  next?: ParameterValue
) {
  if (!next) {
    return;
  }
  const { lower_bound: currentLower, upper_bound: currentUpper } = current;
  const { lower_bound: nextLower, upper_bound: nextUpper } = next;
  const currentValue = currentUpper ?? currentLower ?? 0;
  const nextValue = nextLower ?? nextUpper ?? undefined;
  if (!nextValue) {
    return;
  }
  if (Math.abs(currentValue - nextValue)) {
    return {
      type: currentValue > nextValue ? 'overlap' : 'gap',
      currentValue,
      nextValue,
    };
  }
}
